import React, { useState, useEffect, useMemo } from "react";
import dayjs from "dayjs";
import "dayjs/locale/es";
import { Button, Card, Input, Space, Typography, Row, Col, Skeleton, ConfigProvider } from "antd";
import { SearchOutlined } from "@ant-design/icons";

import { showVisumEffect } from "../../components/utils/antWaveEffect";
import { toast } from "../../components/toasts/toastStore";
import { getMensajesContacto, getMensajeContacto, responderMensajeContacto } from "./mensajesContactoApi";
import "./MensajesContactoPage.css";
import { usePermisos } from "../../context/PermisosContext";

dayjs.locale("es");

const { Title, Text } = Typography;

const fmtDatetime = (v) => {
  if (!v) return "—";
  const d = dayjs(v);
  return d.isValid() ? d.format("D MMM YYYY, HH:mm") : "—";
};

export default function MensajesContactoPage() {
  const { perm } = usePermisos() || { perm: () => true };
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [estadoFilter, setEstadoFilter] = useState("todos");

  const [selectedId, setSelectedId] = useState(null);
  const [mensaje, setMensaje] = useState(null);
  const [loadingDetalle, setLoadingDetalle] = useState(false);

  const [respuestaTexto, setRespuestaTexto] = useState("");
  const [enviando, setEnviando] = useState(false);

  const fetchList = (estado) => {
    setLoading(true);
    getMensajesContacto(estado === "todos" ? undefined : estado)
      .then((res) => setItems(res.data?.items ?? res.data ?? []))
      .catch((err) => toast.error(err?.response?.data?.detail || "No se pudieron cargar los mensajes"))
      .finally(() => setLoading(false));
  };

  useEffect(() => { fetchList(estadoFilter); }, [estadoFilter]);

  useEffect(() => {
    if (!selectedId) { setMensaje(null); return; }
    setLoadingDetalle(true);
    getMensajeContacto(selectedId)
      .then((res) => {
        setMensaje(res.data);
        setRespuestaTexto("");
      })
      .catch(() => toast.error("No se pudo cargar el mensaje"))
      .finally(() => setLoadingDetalle(false));
  }, [selectedId]);

  const counts = useMemo(() => ({
    total: items.length,
    pendientes: items.filter((m) => (m.estado || "pendiente") === "pendiente").length,
    contestados: items.filter((m) => m.estado === "contestado").length,
  }), [items]);

  const filteredItems = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return items;
    return items.filter((m) =>
      (m.nombre_contacto || "").toLowerCase().includes(q) ||
      (m.correo || "").toLowerCase().includes(q) ||
      (m.contenido || "").toLowerCase().includes(q)
    );
  }, [items, search]);

  const STAT_CARDS = [
    { key: "todos", label: "Total de mensajes", icon: <i className="fa-solid fa-inbox" /> },
    { key: "pendiente", label: "Pendientes", icon: <i className="fa-solid fa-envelope" /> },
    { key: "contestado", label: "Contestados", icon: <i className="fa-solid fa-envelope-open-text" /> },
  ];
  const statCount = { todos: counts.total, pendiente: counts.pendientes, contestado: counts.contestados };

  const handleResponder = async () => {
    if (!respuestaTexto.trim()) return toast.warning("Escribe una respuesta antes de enviarla");
    setEnviando(true);
    try {
      await responderMensajeContacto(selectedId, { respuesta: respuestaTexto.trim() });
      toast.success("Respuesta enviada");
      const res = await getMensajeContacto(selectedId);
      setMensaje(res.data);
      fetchList(estadoFilter);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No se pudo enviar la respuesta");
    } finally {
      setEnviando(false);
    }
  };

  return (
    <ConfigProvider wave={{ showEffect: showVisumEffect }}>
      <main className="msg-main">
        <div className="msg-content">
          <div className="msg-filters-panel" style={{ background: "#fff", border: "1px solid #e2e8f0", borderRadius: 16, padding: 18, boxShadow: "0 2px 10px rgba(15,34,58,0.06)", marginBottom: 18 }}>
            <Space direction="vertical" size={2} style={{ marginBottom: 16 }}>
              <Title level={2} className="msg-title">Mensajes de contacto</Title>
              <Text className="msg-subtitle">Bandeja de entrada de los mensajes enviados desde el sitio.</Text>
            </Space>
            <Row gutter={[16, 14]}>
              <Col xs={24}>
                <div className="msg-field-label">Buscador</div>
                <Input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Buscar por nombre, correo o contenido..."
                  suffix={<SearchOutlined className="msg-input-suffix" />}
                  className="msg-control"
                  allowClear
                />
              </Col>
            </Row>
          </div>

          <div className="msg-stats-row" style={{ gridTemplateColumns: "repeat(3, minmax(0, 1fr))" }}>
            {STAT_CARDS.map(({ key, label, icon }) => (
              <Card
                key={key}
                className={`msg-stat-card ${estadoFilter === key ? "msg-stat-active" : ""}`}
                hoverable
                onClick={() => setEstadoFilter((p) => (p === key ? "todos" : key))}
              >
                <Space align="center" size={10}>
                  <div className={`msg-stat-icon ${key === "pendiente" ? "msg-stat-icon-cat" : key === "contestado" ? "msg-stat-icon-doc" : "msg-stat-icon-total"}`}>{icon}</div>
                  <div>
                    <div className="msg-stat-value">{statCount[key]}</div>
                    <div className="msg-stat-label">{label}</div>
                  </div>
                </Space>
              </Card>
            ))}
          </div>

          <div className="msg-toolbar">
            <div className="msg-toolbar-left">
              <h4>Mensajes ({filteredItems.length})</h4>
            </div>
          </div>

          {!loading && filteredItems.length === 0 ? (
            <div className="msg-empty">Sin mensajes que coincidan con los filtros.</div>
          ) : (
            <div className="msg-split">
              <div className="msg-list-col">
                {loading && Array.from({ length: 3 }).map((_, i) => (
                  <div key={`sk-${i}`} className="msg-card">
                    <div className="msg-card-accent" />
                    <div className="msg-card-body">
                      <Skeleton active title={{ width: "60%" }} paragraph={{ rows: 1 }} />
                    </div>
                  </div>
                ))}

                {!loading && filteredItems.map((m) => {
                  const id = m.id_mensaje;
                  const estado = m.estado || "pendiente";
                  const isSel = selectedId === id;
                  return (
                    <div
                      key={id}
                      className={`msg-card ${isSel ? "msg-card-active" : ""} ${estado === "pendiente" ? "msg-card-unread" : ""}`}
                      onClick={() => setSelectedId(id)}
                    >
                      <div className="msg-card-accent" />
                      <div className="msg-card-body">
                        <div className="msg-card-main">
                          <div className="msg-card-desc">{m.nombre_contacto || "Sin nombre"}</div>
                          <div className="msg-card-meta">
                            <span className="msg-card-meta-item"><i className="fa-solid fa-calendar" /> {fmtDatetime(m.fecha)}</span>
                          </div>
                          <div className="msg-card-notes">{m.contenido}</div>
                        </div>
                        <div className="msg-card-right">
                          <span className={`msg-status-badge msg-status-${estado}`}>
                            {estado === "pendiente" ? "Pendiente" : "Contestado"}
                          </span>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              <div className="msg-detail-panel">
                {!selectedId ? (
                  <div className="msg-detail-empty">
                    <i className="fa-solid fa-envelope-open-text msg-detail-empty-icon" />
                    <div>Selecciona un mensaje de la lista para ver su contenido y responder.</div>
                  </div>
                ) : loadingDetalle || !mensaje ? (
                  <Skeleton active paragraph={{ rows: 5 }} />
                ) : (
                  <>
                    <div className="msg-detail-header">
                      <div>
                        <div className="msg-detail-from">{mensaje.nombre_contacto || "Sin nombre"}</div>
                        <div className="msg-detail-meta">
                          <span><i className="fa-solid fa-at" /> {mensaje.correo}</span>
                          {mensaje.telefono && <span><i className="fa-solid fa-phone" /> {mensaje.telefono}</span>}
                          <span><i className="fa-solid fa-calendar" /> {fmtDatetime(mensaje.fecha)}</span>
                        </div>
                      </div>
                      <span className={`msg-status-badge msg-status-${mensaje.estado || "pendiente"}`}>
                        {(mensaje.estado || "pendiente") === "pendiente" ? "Pendiente" : "Contestado"}
                      </span>
                    </div>

                    <div className="msg-detail-body">{mensaje.contenido}</div>

                    {mensaje.estado === "contestado" ? (
                      <div className="msg-respuesta-block">
                        <div className="msg-respuesta-label">Mensaje respondido</div>
                        <div className="msg-respuesta-text">
                          Respondido por {mensaje.contestado_por || "un administrador"} el {fmtDatetime(mensaje.fecha_respuesta)}.
                        </div>
                      </div>
                    ) : (
                      <div>
                        <div className="msg-field-label" style={{ marginBottom: 8 }}>Responder</div>
                        <Input.TextArea
                          value={respuestaTexto}
                          onChange={(e) => setRespuestaTexto(e.target.value)}
                          rows={4}
                          maxLength={1000}
                          showCount
                          placeholder="Escribe la respuesta para este mensaje..."
                        />
                        <div style={{ marginTop: 10, textAlign: "right" }}>
                          {perm("mensajes_contacto", "editar") && (
                            <Button
                            type="primary"
                            icon={<i className="fa-solid fa-paper-plane" />}
                            loading={enviando}
                            onClick={handleResponder}
                            style={{ backgroundColor: "var(--brand-primary)", borderColor: "var(--brand-primary)" }}
                          >
                            Responder
                          </Button>
                          )}
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>
          )}
        </div>
      </main>
    </ConfigProvider>
  );
}
